import Vue from 'vue'
import Vuex from 'vuex'
import mutations from './mutations'
import actions from './actions'
import createPersistedState from 'vuex-persistedstate'
import { SessionStatus, InputState, SessionResult, PopupCloseSetting, LabelTypes } from '@/constants'

Vue.use(Vuex)

function getPersistKey() {
  const params = new URLSearchParams(window.location.search)
  const isChatbot = params.get('is_chatbot_embed')
  if (isChatbot) {
    return 'ivrpath-chatbot'
  }
  return 'ivrpath'
}

export default new Vuex.Store({
  plugins: [createPersistedState({
    key: getPersistKey()
  })],
  state: {
    status: '',
    token: '',
    campaign: null,
    session: null,
    popupDisplayState: null,
    messages: [],
    warnings: [],
    firstLogin: false,
    isLoading: false,
    isCPLoading: false,
    initialLoading: true,
    error: null,
    warning: null,
    selectedItems: [],
    selectedItemIndex: 0,
    statusItems: [],
    startIndex: 0,
    undoCount: 0,
    idleTimer: null,
    criticalError: null,
    lifelineCallback: null,
    faqsLoading: false,
    allFaqTopics: [],
    profilerEnabled: false,
    cprofilerEnabled: false,
    debug: false,
    inputState: InputState.Unknown,
    currentLanguage: 'en',
    isLanguageReady: false,
    styleVariables: {},
    styleLabels: {},
    style: '',
    styleUrl: null,
    faviconUrl: null,
    votedFaqs: {},
    selectedFaq: null,
    relatedFaqs: [],
    isIE: false,
    faqSearchResults: [],
    previouslySelectedFaqTopic: null,
    selectedFaqTopic: null,
    cart: [],
    shipping: [],
    tax: null,
    gateway: null,
    cartIsOpen: false,
    payment_sources: [],
    checkoutErrors: null,
    forceDisplayLoader: false,
    refreshSessionOnUndo: false,
    captchaLoaded: false,
    fulfillments: [],
    isMatchVerifyError: [],
    showChatbotHeader: false,
    // used for keeping track of popup steps separately so that old step could keep showing in background
    popupSession: null,
    isPopupLoading: false,
    isBannerHidden: false,
    isLabelHidden: false,
    isContentHidden: false,
    isMediaHidden: false,
    subscriptionOnly: false,
    // step stack for tracking navigation history
    stepStack: []
  },
  getters: {
    isSessionStarted: function(state) {
      return !!state.token
    },
    isError: function(state) {
      return state.status === 'error'
    },
    isSessionValid: function(state, getters) {
      return getters.isSessionStarted && !!state.session
    },
    isSessionComplete: function(state) {
      return state.session && state.session.result === SessionResult.Complete
    },
    mustLogoutSession: function(state) {
      return state.session &&
        ([SessionResult.LifelineNoVerify, SessionResult.LifelineLocked].indexOf(state.session.result) !== -1)
    },
    isSessionActive: function(state, getters) {
      return getters.isSessionValid && state.session.is_active
    },
    isSessionActiveAndReady: function(state, getters) {
      return getters.isSessionActive && !state.isLoading
    },
    canLogoutSession: function(state, getters) {
      return getters.isSessionValid &&
        state.session.result === SessionResult.Active && state.session.status !== SessionStatus.Unmatched
    },
    isSessionUserKnown: function(state, getters) {
      return getters.isSessionValid && state.session.customer && state.session.is_verified && state.session.is_active
    },
    isLastItem: function(state) {
      return state.selectedItems.length &&
        (state.selectedItemIndex === state.selectedItems.length - 1)
    },
    currentSelectedItem: function(state) {
      if (state.selectedItems.length &&
        (state.selectedItemIndex >= 0) &&
        (state.selectedItemIndex < state.selectedItems.length)) {
        return state.selectedItems[state.selectedItemIndex]
      }

      return null
    },
    canGotoPreviousItem: function(state) {
      return state.selectedItemIndex > state.startIndex
    },
    isMainMenu: function(state, getters) {
      return getters.isSessionActiveAndReady && state.session.is_first_step &&
        ((state.inputState === InputState.Unknown) || (state.inputState === InputState.OkToSubmit))
    },
    processingGIF: function(state) {
      if (state.campaign && state.campaign.processing_gif) {
        return state.campaign.processing_gif.file
      }

      return require('@/assets/images/default_processing_gif.gif')
    },
    isLifelineActive: function(state) {
      return state.session && state.session.is_lifeline_active && !state.session.show_lifeline_contact
    },
    showContactForm: function(state, getters) {
      return !!state.criticalError || (getters.isSessionActive && state.session.show_lifeline_contact)
    },
    checkoutDisabled: function(state) {
      return !state.session || !state.session.has_checkout || process.env.VUE_APP_CHECKOUT_DISABLED === 'true'
    },
    isAlreadySessionUrl: function() {
      return window.location.pathname === '/session'
    },
    popupCloseCanUndo: function(state) {
      return state.popupSession && state.popupSession.can_undo &&
        ([
          PopupCloseSetting.Undo, PopupCloseSetting.UndoOrFirstInput, PopupCloseSetting.UndoOrLastInput
        ].indexOf(state.popupSession.popup_close_setting) !== -1)
    },
    popupCloseCanSelectFirstInput: function(state) {
      return state.popupSession && state.popupSession.valid_inputs && state.popupSession.valid_inputs.length &&
        ([
          PopupCloseSetting.FirstInput, PopupCloseSetting.UndoOrFirstInput
        ].indexOf(state.popupSession.popup_close_setting) !== -1)
    },
    popupCloseCanSelectLastInput: function(state) {
      return state.popupSession && state.popupSession.valid_inputs && state.popupSession.valid_inputs.length &&
        ([
          PopupCloseSetting.LastInput, PopupCloseSetting.UndoOrLastInput
        ].indexOf(state.popupSession.popup_close_setting) !== -1)
    },
    subscriptionStatusLabels: function(state) {
      return (state.styleLabels || {})[LabelTypes.SubscriptionStatus] || {}
    },
    activeSession: function(state) {
      return (state.session && state.session.is_popup && state.popupSession) ? state.popupSession : state.session
    }
  },
  mutations,
  actions
})
